/* Losses and expenditures, from the engine (`FR-ANAL-09`).
 *
 * This replaces `data/attrition.ts`, which seeded the panel with four scripted kills. The fixture
 * is deleted rather than kept as a fallback: an attrition table is a claim about what happened in
 * *this* run, and a plausible one drawn from a fixture is indistinguishable from a real one until
 * somebody checks a name against the map. When the Gateway is unreachable the panel says so and
 * shows nothing — the honest answer to "how many have we lost" is not four.
 *
 * ── The reduction lives on the server ──────────────────────────────────────
 *
 * Aggregation is the Gateway's job (`orbis_gateway/attrition.py`), not this file's, for two
 * reasons. The live event stream retains roughly 72 ticks, so a client folding the tail would
 * watch its own casualty list shrink; and side scoping has to happen before the wire, because a
 * client that receives RED's magazine state has already been told it.
 */

import { authHeaders, baseUrl } from './gateway'

export interface Loss {
  id: string
  side: string
  /** Own losses by name; a claimed kill by the track label this side gave it. */
  designation: string
  className: string
  domain: string
  tick: number
  simTimeNs: number
  /** Machine-readable, as the engine emits it — `destroyed`, `fuel_exhausted`. */
  cause: string
  weapon: string
  /** Empty when nothing was attributed, or when the shooter is on a side this client cannot see. */
  killedBy: string
}

export interface Expenditure {
  id: string
  side: string
  weapon: string
  category: string
  fired: number
  hits: number
  /** Absent when no living platform on a visible side still carries this weapon — which is not
   *  the same as an empty magazine, and must not render as zero rounds left. */
  remaining?: number
}

export interface Attrition {
  losses: Loss[]
  expenditures: Expenditure[]
}

export const EMPTY_ATTRITION: Attrition = { losses: [], expenditures: [] }

/** Wording for the engine's cause identifiers. Unknown causes render raw rather than being
 *  dropped — a new cause must show up immediately, even if it reads as snake case. */
const CAUSE_LABELS: Record<string, string> = {
  destroyed: 'warhead detonation',
  fuel_exhausted: 'fuel exhaustion',
  mission_kill: 'mission kill',
}

export function causeLabel(loss: Loss): string {
  const cause = CAUSE_LABELS[loss.cause] ?? loss.cause.replace(/_/g, ' ')
  return loss.weapon ? `${loss.weapon} · ${cause}` : cause
}

/** Sim clock for a loss, in the same format the log dock and trace panel use. */
export function lossClock(epochIso: string, simTimeNs: number): string {
  const ms = Date.parse(epochIso) + (simTimeNs ?? 0) / 1e6
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(11, 19) : '--:--:--'
}

export async function fetchAttrition(): Promise<Attrition> {
  const res = await fetch(`${baseUrl()}/v1/attrition`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const body = (await res.json()) as Partial<Attrition>
  return { losses: body.losses ?? [], expenditures: body.expenditures ?? [] }
}
